import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemText } from '@mui/material';

function AntiFraudPolicy() {
  return (
    <Container maxWidth="md">
      <Box sx={{ padding: '20px' }}>
        <Typography variant="h5" gutterBottom>
          FM Digital Official Anti-Fraud Policy
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Effective Date: (August 2024)
        </Typography>
        <Typography variant="body1" gutterBottom>
          FM Digital Official is committed to conducting business in an ethical and transparent manner. We maintain a zero-tolerance policy towards any form of fraud, dishonesty, or unethical behavior. This Anti-Fraud Policy outlines the procedures we follow to prevent, detect, and respond to fraudulent activities.
        </Typography>

        <Typography variant="h6" gutterBottom>
          1. Purpose of the Policy
        </Typography>
        <Typography variant="body1" gutterBottom>
          The purpose of this Anti-Fraud Policy is to:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Safeguard FM Digital Official’s assets and reputation." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Prevent fraudulent activities." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Ensure a culture of honesty and accountability within FM Digital Official." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Provide guidelines for detecting and addressing fraud." />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom>
          2. Definition of Fraud
        </Typography>
        <Typography variant="body1" gutterBottom>
          Fraud is defined as any intentional act or omission designed to deceive others, resulting in financial or other gain at the expense of FM Digital Official, its employees, partners, or clients. Fraud may include, but is not limited to:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Forgery or alteration of documents." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Misrepresentation of financial information." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Unauthorized use of company assets." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Theft or embezzlement." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Bribery or corruption." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Falsification of records." />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom>
          3. Responsibilities
        </Typography>
        <Typography variant="body1" gutterBottom>
          3.1 All Employees and Contractors:
        </Typography>
        <Typography variant="body2" gutterBottom>
          Each employee and contractor of FM Digital Official is responsible for safeguarding the company’s resources and reporting any suspected fraudulent activities. This policy applies to everyone, regardless of their role or position.
        </Typography>

        <Typography variant="body1" gutterBottom>
          3.2 Management:
        </Typography>
        <Typography variant="body2" gutterBottom>
          Management at all levels is responsible for ensuring internal controls are in place to prevent and detect fraud. They must foster a culture of transparency, integrity, and ethical behavior.
        </Typography>

        <Typography variant="body1" gutterBottom>
          3.3 Compliance Department:
        </Typography>
        <Typography variant="body2" gutterBottom>
          FM Digital Official’s compliance team is responsible for monitoring, investigating, and addressing any suspected fraudulent activities. They will work with other departments as necessary to ensure compliance with this policy.
        </Typography>

        <Typography variant="h6" gutterBottom>
          4. Reporting Fraud
        </Typography>
        <Typography variant="body1" gutterBottom>
          FM Digital Official encourages all employees, partners, and clients to report any suspicious or fraudulent activities. Reports can be made in confidence via the following methods:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Email: [Insert Email]" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Phone: [Insert Phone Number]" />
          </ListItem>
          <ListItem>
            <ListItemText primary="In-Person Reporting: Contact your immediate supervisor or the compliance department." />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom>
          5. Investigation Process
        </Typography>
        <Typography variant="body1" gutterBottom>
          When a report of fraud is received, FM Digital Official will conduct a thorough investigation. The process includes:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Assessment: The compliance team will assess the report and determine the need for an investigation." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Investigation: An independent investigation team will gather relevant evidence, including reviewing documents, interviewing witnesses, and analyzing financial records." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Outcome: Based on the findings, FM Digital Official will take appropriate action, which may include termination of employment, legal action, or other disciplinary measures." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Reporting: Findings will be documented, and corrective measures will be implemented where necessary to prevent future occurrences." />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom>
          6. Fraud Prevention Measures
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Regular audits of financial records and processes." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Internal controls to monitor and approve transactions." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Clear separation of duties to prevent conflicts of interest." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Staff training on ethical behavior and fraud prevention." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Whistleblower protections for those reporting fraudulent activities." />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom>
          7. Consequences of Fraud
        </Typography>
        <Typography variant="body1" gutterBottom>
          Any employee, contractor, or partner found guilty of committing fraud will face disciplinary action, which may include:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Immediate termination of employment or contract." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Legal proceedings, including criminal charges if necessary." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Financial restitution for any losses incurred by FM Digital Official." />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom>
          8. Third-Party Fraud
        </Typography>
        <Typography variant="body1" gutterBottom>
          FM Digital Official also takes steps to prevent fraud by third parties, including partners, vendors, and clients. We require all external partners to adhere to the highest standards of ethical behavior. Any suspected fraud by third parties will be investigated, and appropriate action will be taken, including termination of business relationships or legal action.
        </Typography>

        <Typography variant="h6" gutterBottom>
          9. Review of the Policy
        </Typography>
        <Typography variant="body1" gutterBottom>
          This Anti-Fraud Policy will be reviewed annually to ensure it remains up to date with legal requirements and best practices.
        </Typography>

        <Typography variant="h6" gutterBottom>
          10. Contact Information
        </Typography>
        <Typography variant="body1" gutterBottom>
          For further questions regarding this Anti-Fraud Policy or to report suspicious activities, please contact:
        </Typography>
        <Typography variant="body1">
          FM Digital Official <br />
          India, Asia
        </Typography>
      </Box>
    </Container>
  );
}

export default AntiFraudPolicy;
